// Enhanced Agency Registration Component with Tax Forms for Travel+ CRM
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import W9Form from '../TaxForms/W9Form';
import W8BENForm from '../TaxForms/W8BENForm';
import IdentityVerificationStep from './IdentityVerificationStep';
import { normalizePhoneE164, isE164 } from '../../utils/phone';
import { MapPin, Building, FileText, Check } from 'lucide-react';

// US States array for dropdown
const usStates = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'District of Columbia' }
];

export const AgencyRegistrationWithTax = ({ agency, agencyCode, onSuccess }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1); // position in `steps`: personal, [identity], tax, review
  const [identityRequired, setIdentityRequired] = useState(false);
  const [identityVerification, setIdentityVerification] = useState(null);
  const [phoneVerification, setPhoneVerification] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    password: '',
    confirmPassword: '',
    agencyCode: agencyCode || '',
    agencyName: agency || '',
    agreeToTerms: false,
    digitalSignature: ''
  });

  const [taxFormData, setTaxFormData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [agencyTerms, setAgencyTerms] = useState('');

  // List of US states for the dropdown
  const usStates = [
    { code: 'AL', name: 'Alabama' },
    { code: 'AK', name: 'Alaska' },
    { code: 'AZ', name: 'Arizona' },
    { code: 'AR', name: 'Arkansas' },
    { code: 'CA', name: 'California' },
    { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' },
    { code: 'DE', name: 'Delaware' },
    { code: 'DC', name: 'District of Columbia' },
    { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' },
    { code: 'HI', name: 'Hawaii' },
    { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' },
    { code: 'IN', name: 'Indiana' },
    { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' },
    { code: 'KY', name: 'Kentucky' },
    { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' },
    { code: 'MD', name: 'Maryland' },
    { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' },
    { code: 'MN', name: 'Minnesota' },
    { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' },
    { code: 'MT', name: 'Montana' },
    { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' },
    { code: 'NH', name: 'New Hampshire' },
    { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' },
    { code: 'NY', name: 'New York' },
    { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' },
    { code: 'OH', name: 'Ohio' },
    { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' },
    { code: 'PA', name: 'Pennsylvania' },
    { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' },
    { code: 'SD', name: 'South Dakota' },
    { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' },
    { code: 'UT', name: 'Utah' },
    { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' },
    { code: 'WA', name: 'Washington' },
    { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' },
    { code: 'WY', name: 'Wyoming' }
  ];

  // List of countries for the dropdown
  const countries = [
    { code: 'US', name: 'United States' },
    { code: 'PR', name: 'Puerto Rico' },
    { code: 'CA', name: 'Canada' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'AU', name: 'Australia' },
    { code: 'DE', name: 'Germany' },
    { code: 'FR', name: 'France' },
    { code: 'IT', name: 'Italy' },
    { code: 'ES', name: 'Spain' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'BE', name: 'Belgium' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'AT', name: 'Austria' },
    { code: 'SE', name: 'Sweden' },
    { code: 'NO', name: 'Norway' },
    { code: 'DK', name: 'Denmark' },
    { code: 'FI', name: 'Finland' },
    { code: 'IE', name: 'Ireland' },
    { code: 'PT', name: 'Portugal' },
    { code: 'GR', name: 'Greece' },
    { code: 'JP', name: 'Japan' },
    { code: 'KR', name: 'South Korea' },
    { code: 'SG', name: 'Singapore' },
    { code: 'HK', name: 'Hong Kong' },
    { code: 'NZ', name: 'New Zealand' },
    { code: 'MX', name: 'Mexico' },
    { code: 'BR', name: 'Brazil' },
    { code: 'AR', name: 'Argentina' },
    { code: 'CL', name: 'Chile' },
    { code: 'CR', name: 'Costa Rica' },
    { code: 'NI', name: 'Nicaragua' },
    { code: 'SV', name: 'El Salvador' },
    { code: 'HN', name: 'Honduras' },
    { code: 'GT', name: 'Guatemala' },
    { code: 'BZ', name: 'Belize' },
    { code: 'ZA', name: 'South Africa' },
    { code: 'IN', name: 'India' },
    { code: 'CN', name: 'China' },
    { code: 'TH', name: 'Thailand' },
    { code: 'MY', name: 'Malaysia' },
    { code: 'PH', name: 'Philippines' },
    { code: 'ID', name: 'Indonesia' },
    { code: 'VN', name: 'Vietnam' },
    { code: 'AE', name: 'United Arab Emirates' },
    { code: 'SA', name: 'Saudi Arabia' },
    { code: 'IL', name: 'Israel' },
    { code: 'TR', name: 'Turkey' },
    { code: 'EG', name: 'Egypt' },
    { code: 'MA', name: 'Morocco' },
    { code: 'KE', name: 'Kenya' },
    { code: 'NG', name: 'Nigeria' },
    { code: 'GH', name: 'Ghana' },
    { code: 'OTHER', name: 'Other' }
  ];

  // Fetch agency-specific terms
  useEffect(() => {
    if (!agencyCode) return;
    api.get(`/registration/identity/config/${agencyCode}`)
      .then(res => setIdentityRequired(!!res.data.required))
      .catch(() => setIdentityRequired(false));
  }, [agencyCode]);

  useEffect(() => {
    const fetchAgencyTerms = async () => {
      try {
        const response = await api.get(`/registration/agencies/${agencyCode}/terms`);
        setAgencyTerms(response.data.terms);
      } catch (error) {
        console.log('Using default terms');
      }
    };
    
    if (agencyCode) {
      fetchAgencyTerms();
    }
  }, [agencyCode]);

  const validatePassword = (password) => {
    const requirements = [];
    if (password.length < 8) requirements.push('At least 8 characters');
    if (!/(?=.*[a-z])/.test(password)) requirements.push('One lowercase letter');
    if (!/(?=.*[A-Z])/.test(password)) requirements.push('One uppercase letter');
    if (!/(?=.*\d)/.test(password)) requirements.push('One number');
    if (!/(?=.*[@$!%*?&])/.test(password)) requirements.push('One special character (@$!%*?&)');
    if (!/^[A-Za-z\d@$!%*?&]+$/.test(password)) requirements.push('Only letters, numbers, and @$!%*?& allowed');
    return requirements;
  };

  const validatePersonalInfo = () => {
    const newErrors = {};
    
    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.phone) newErrors.phone = 'Phone number is required';
    else if (identityRequired && !isE164(normalizePhoneE164(formData.phone, formData.country))) {
      newErrors.phone = 'Enter your mobile number in international format, for example +1 555 123 4567';
    }
    if (!formData.address) newErrors.address = 'Street address is required';
    if (!formData.city) newErrors.city = 'City is required';
    if (!formData.state) newErrors.state = 'State/Province is required';
    if (!formData.zipCode && formData.country !== 'BZ') newErrors.zipCode = 'ZIP/Postal code is required';
    if (!formData.country) newErrors.country = 'Country is required';
    
    // Enhanced password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else {
      const passwordErrors = validatePassword(formData.password);
      if (passwordErrors.length > 0) {
        newErrors.password = `Password must contain: ${passwordErrors.join(', ')}`;
      }
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (!formData.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    
    if (!formData.digitalSignature || formData.digitalSignature.trim().length < 2) {
      newErrors.digitalSignature = 'Please type your full name as your digital signature';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const steps = identityRequired
    ? [
        { key: 'personal', label: 'Personal Info', title: 'Personal Information' },
        { key: 'identity', label: 'Verify Identity', title: 'Identity Verification' },
        { key: 'tax', label: 'Tax Form', title: 'Tax Form' },
        { key: 'review', label: 'Review & Submit', title: 'Review & Submit' }
      ]
    : [
        { key: 'personal', label: 'Personal Info', title: 'Personal Information' },
        { key: 'tax', label: 'Tax Form', title: 'Tax Form' },
        { key: 'review', label: 'Review & Submit', title: 'Review & Submit' }
      ];
  const totalSteps = steps.length;
  const stepKey = steps[currentStep - 1]?.key;
  const nextAfterPersonal = identityRequired ? 'Next: Verify Your Identity' : 'Next: Complete Tax Form';

  const handleNextStep = () => {
    if (currentStep === 1 && validatePersonalInfo()) {
      const phone = identityRequired ? normalizePhoneE164(formData.phone, formData.country) : formData.phone;
      if (phone !== formData.phone) setFormData(prev => ({ ...prev, phone }));
      const v = identityVerification;
      if (v && (v.email !== formData.email || v.firstName !== formData.firstName || v.lastName !== formData.lastName || v.phone !== phone)) {
        setIdentityVerification(null);
      }
      const p = phoneVerification;
      if (p && (p.email !== formData.email || p.phone !== phone)) {
        setPhoneVerification(null);
      }
      setCurrentStep(2);
    }
  };

  const handleTaxFormComplete = (taxData) => {
    setTaxFormData(taxData);
    setCurrentStep(totalSteps);
  };

  const handleFinalSubmit = async () => {
    setLoading(true);
    setErrors({});

    try {
      // Combine all data for submission
      const submissionData = {
        ...formData,
        taxFormData: taxFormData,
        taxFormType: ['US', 'PR'].includes(formData.country) ? 'W-9' : 'W-8BEN',
        ...(identityRequired && { identityVerificationId: identityVerification?.verificationId })
      };

      const response = await api.post('/registration/agency', submissionData);
      // Navigate to success page instead of showing inline success
      navigate('/registration-success', {
        state: { agency, agencyCode }
      });
      if (onSuccess) onSuccess(response.data);
    } catch (error) {
      if (error.response?.data?.errors) {
        const errorObj = {};
        error.response.data.errors.forEach(err => {
          errorObj[err.param] = err.msg;
        });
        setErrors(errorObj);
      } else {
        setErrors({ general: error.response?.data?.message || 'Registration failed' });
      }
      // Don't go back to step 1 - stay on current step to show error
    } finally {
      setLoading(false);
    }
  };

  // Remove the submitted check as we're navigating away

  const renderStepIndicator = () => (
    <div className="mb-8">
      {/* Desktop Step Indicator */}
      <div className="hidden md:flex items-center justify-center">
        {steps.map((step, index) => {
          const number = index + 1;
          return (
            <React.Fragment key={step.key}>
              {index > 0 && (
                <div className="mx-4 w-16 h-1 bg-gray-300">
                  <div className={`h-full bg-blue-600 transition-all ${
                    currentStep >= number ? 'w-full' : 'w-0'
                  }`} />
                </div>
              )}
              <div className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                  currentStep >= number ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
                }`}>
                  {currentStep > number ? <Check className="w-5 h-5" /> : number}
                </div>
                <span className="ml-2 text-sm font-medium text-gray-900">{step.label}</span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
      
      {/* Mobile Step Indicator */}
      <div className="md:hidden">
        <div className="flex justify-center mb-4">
          <div className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full flex items-center justify-center text-lg font-semibold bg-blue-600 text-white">
              {currentStep > 1 ? <Check className="w-6 h-6" /> : '1'}
            </div>
            <p className="mt-2 text-sm font-medium text-gray-900">Step {currentStep} of {totalSteps}</p>
            <p className="text-xs text-gray-600">{steps[currentStep - 1]?.title}</p>
          </div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(currentStep / totalSteps) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );

  const renderPersonalInfoStep = () => (
    <div className="bg-white p-6 sm:p-8 rounded-lg shadow-lg">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Register with {agency}</h2>
        <p className="text-gray-600">Please fill out the following information to join Travel+ CRM with your Travel Agency.</p>
      </div>

      {errors.general && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700">{errors.general}</p>
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); handleNextStep(); }} className="space-y-6">
        
        {/* Personal Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Personal Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name *
              </label>
              <input 
                type="text" 
                required
                value={formData.firstName}
                onChange={(e) => setFormData(prev => ({...prev, firstName: e.target.value}))}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.firstName ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.firstName && <p className="text-red-600 text-sm mt-1">{errors.firstName}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name *
              </label>
              <input 
                type="text" 
                required
                value={formData.lastName}
                onChange={(e) => setFormData(prev => ({...prev, lastName: e.target.value}))}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.lastName ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.lastName && <p className="text-red-600 text-sm mt-1">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address *
              </label>
              <input 
                type="email" 
                required
                value={formData.email}
                onChange={(e) => setFormData(prev => ({...prev, email: e.target.value}))}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.email ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number *
              </label>
              <input 
                type="tel" 
                required
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({...prev, phone: e.target.value}))}
                onBlur={() => identityRequired && formData.phone && setFormData(prev => ({ ...prev, phone: normalizePhoneE164(prev.phone, prev.country) }))}
                placeholder={identityRequired ? '+1 555 123 4567' : undefined}
                autoComplete="tel"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.phone ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {identityRequired && !errors.phone && (
                <p className="text-xs text-gray-600 mt-1">
                  Mobile number in international format. We will text it a verification code, so landline and VoIP numbers cannot be used.
                </p>
              )}
              {errors.phone && <p className="text-red-600 text-sm mt-1">{errors.phone}</p>}
            </div>
          </div>
        </div>

        {/* Password Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            Account Security
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password *
              </label>
              <input 
                type="password" 
                required
                value={formData.password}
                onChange={(e) => setFormData(prev => ({...prev, password: e.target.value}))}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.password ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter your password"
              />
              
              {/* Password Requirements Display */}
              <div className="mt-2 text-xs text-gray-600">
                <p className="font-medium mb-1">Password must contain:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                  <div className={`flex items-center ${formData.password.length >= 8 ? 'text-green-600' : 'text-gray-500'}`}>
                    <span className="mr-2 flex-shrink-0">{formData.password.length >= 8 ? '✓' : '○'}</span>
                    <span className="text-xs">At least 8 characters</span>
                  </div>
                  <div className={`flex items-center ${/(?=.*[a-z])/.test(formData.password) ? 'text-green-600' : 'text-gray-500'}`}>
                    <span className="mr-2 flex-shrink-0">{/(?=.*[a-z])/.test(formData.password) ? '✓' : '○'}</span>
                    <span className="text-xs">One lowercase (a-z)</span>
                  </div>
                  <div className={`flex items-center ${/(?=.*[A-Z])/.test(formData.password) ? 'text-green-600' : 'text-gray-500'}`}>
                    <span className="mr-2 flex-shrink-0">{/(?=.*[A-Z])/.test(formData.password) ? '✓' : '○'}</span>
                    <span className="text-xs">One uppercase (A-Z)</span>
                  </div>
                  <div className={`flex items-center ${/(?=.*\d)/.test(formData.password) ? 'text-green-600' : 'text-gray-500'}`}>
                    <span className="mr-2 flex-shrink-0">{/(?=.*\d)/.test(formData.password) ? '✓' : '○'}</span>
                    <span className="text-xs">One number (0-9)</span>
                  </div>
                  <div className={`flex items-center sm:col-span-2 ${/(?=.*[@$!%*?&])/.test(formData.password) ? 'text-green-600' : 'text-gray-500'}`}>
                    <span className="mr-2 flex-shrink-0">{/(?=.*[@$!%*?&])/.test(formData.password) ? '✓' : '○'}</span>
                    <span className="text-xs">One special character (@$!%*?&)</span>
                  </div>
                </div>
              </div>
              
              {errors.password && <p className="text-red-600 text-sm mt-1">{errors.password}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password *
              </label>
              <input 
                type="password" 
                required
                value={formData.confirmPassword}
                onChange={(e) => setFormData(prev => ({...prev, confirmPassword: e.target.value}))}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Confirm your password"
              />
              {errors.confirmPassword && <p className="text-red-600 text-sm mt-1">{errors.confirmPassword}</p>}
            </div>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              <strong>Security Notice:</strong> Your password will be securely stored and you'll be able to login immediately after your application is approved by the agency administrator.
            </p>
          </div>
        </div>

        {/* Address Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 flex items-center">
            <MapPin className="w-5 h-5 mr-2" />
            Address Information
          </h3>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Street Address *
            </label>
            <input 
              type="text"
              required
              value={formData.address}
              onChange={(e) => setFormData(prev => ({...prev, address: e.target.value}))}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.address ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="123 Main Street"
            />
            {errors.address && <p className="text-red-600 text-sm mt-1">{errors.address}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="sm:col-span-2 lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City *
              </label>
              <input 
                type="text"
                required
                value={formData.city}
                onChange={(e) => setFormData(prev => ({...prev, city: e.target.value}))}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.city ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="City"
              />
              {errors.city && <p className="text-red-600 text-sm mt-1">{errors.city}</p>}
            </div>
            <div className="sm:col-span-2 lg:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State/Province *
              </label>
              {formData.country === 'US' ? (
                <select
                  required
                  value={formData.state}
                  onChange={(e) => setFormData(prev => ({...prev, state: e.target.value}))}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.state ? 'border-red-300' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select State</option>
                  {usStates.map(state => (
                    <option key={state.code} value={state.name}>
                      {state.name}
                    </option>
                  ))}
                </select>
              ) : (
                <input 
                  type="text"
                  required
                  value={formData.state}
                  onChange={(e) => setFormData(prev => ({...prev, state: e.target.value}))}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.state ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="State/Province"
                />
              )}
              {errors.state && <p className="text-red-600 text-sm mt-1">{errors.state}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ZIP/Postal Code {formData.country !== 'BZ' && '*'}
              </label>
              <input 
                type="text"
                required={formData.country !== 'BZ'}
                value={formData.zipCode}
                onChange={(e) => setFormData(prev => ({...prev, zipCode: e.target.value}))}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.zipCode ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="ZIP/Postal Code"
              />
              {errors.zipCode && <p className="text-red-600 text-sm mt-1">{errors.zipCode}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country *
              </label>
              <select
                required
                value={formData.country}
                onChange={(e) => setFormData(prev => ({...prev, country: e.target.value}))}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.country ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Select Country</option>
                {countries.map(country => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </select>
              {errors.country && <p className="text-red-600 text-sm mt-1">{errors.country}</p>}
            </div>
          </div>

          {/* Tax Form Notice */}
          {formData.country && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>Tax Information:</strong> 
                {['US', 'PR'].includes(formData.country) ? 
                  ' You will need to complete a W-9 tax form in the next step.' :
                  ' You will need to complete a W-8BEN tax form in the next step.'
                }
              </p>
            </div>
          )}
        </div>

        {/* Agency Terms and Conditions */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 flex items-center">
            <Building className="w-5 h-5 mr-2" />
            Agent Agreement
          </h3>
          
          <div className="bg-gray-50 border rounded-lg p-4 max-h-40 overflow-y-auto">
            <h4 className="font-medium text-gray-900 mb-2">{agency} Agent Agreement</h4>
            <div className="text-sm text-gray-700 space-y-2">
              {agencyTerms ? (
                <div dangerouslySetInnerHTML={{ __html: agencyTerms }} />
              ) : (
                <div>
                  <p>By applying to join {agency}, you agree to:</p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Maintain professional standards in all client interactions</li>
                    <li>Follow agency policies and procedures</li>
                    <li>Complete required training and certifications</li>
                    <li>Adhere to commission structure and payment terms</li>
                    <li>Represent the agency with integrity and professionalism</li>
                    <li>Comply with all industry regulations and licensing requirements</li>
                    <li>Complete required tax forms (W-9 for US agents, W-8BEN for international agents)</li>
                  </ul>
                  <p className="font-medium mt-2">Full terms and conditions will be provided upon approval.</p>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-start">
            <input
              type="checkbox"
              required
              checked={formData.agreeToTerms}
              onChange={(e) => setFormData(prev => ({...prev, agreeToTerms: e.target.checked}))}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-1"
            />
            <label className="ml-2 text-sm text-gray-700">
              I have read and agree to the {agency} Agent Agreement and Terms of Service *
            </label>
          </div>
          {errors.agreeToTerms && <p className="text-red-600 text-sm mt-1">{errors.agreeToTerms}</p>}

          {/* Digital Signature Field */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Digital Signature *
            </label>
            <input
              type="text"
              required
              value={formData.digitalSignature}
              onChange={(e) => setFormData(prev => ({...prev, digitalSignature: e.target.value}))}
              placeholder="Type your full name here as your digital signature"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              By typing your name above, you are providing your digital signature to this agreement.
            </p>
            {errors.digitalSignature && <p className="text-red-600 text-sm mt-1">{errors.digitalSignature}</p>}
          </div>
        </div>

        {/* Next Button */}
        <div className="pt-6">
          <button 
            type="submit"
            disabled={!formData.agreeToTerms || !formData.digitalSignature.trim()}
            className={`w-full py-3 px-4 rounded-md text-white font-medium transition-colors ${
              !formData.agreeToTerms || !formData.digitalSignature.trim()
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500'
            }`}
          >
            {nextAfterPersonal}
          </button>
        </div>
      </form>
    </div>
  );

  const renderTaxFormStep = () => {
    // Create a temporary user object for the tax form components
    const tempUser = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      country: formData.country
    };

    return (
      <div className="bg-white rounded-lg shadow-lg">
        <div className="p-6 sm:p-8 border-b">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <FileText className="w-6 h-6 mr-2" />
            Tax Form - {['US', 'PR'].includes(formData.country) ? 'W-9' : 'W-8BEN'}
          </h2>
          <p className="text-gray-600 mt-2">
            Please complete the required tax form to continue with your application.
          </p>
        </div>
        
        <div className="p-6 sm:p-8">
          {formData.country === 'US' ? (
            <W9FormRegistration 
              user={tempUser}
              registrationData={formData}
              onComplete={handleTaxFormComplete}
              onBack={() => setCurrentStep(totalSteps - 2)}
            />
          ) : (
            <W8BENFormRegistration
              user={tempUser}
              registrationData={formData}
              onComplete={handleTaxFormComplete}
              onBack={() => setCurrentStep(totalSteps - 2)}
            />
          )}
        </div>
      </div>
    );
  };

  const renderReviewStep = () => (
    <div className="bg-white p-6 sm:p-8 rounded-lg shadow-lg">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Review Your Application</h2>
        <p className="text-gray-600">Please review your information before submitting.</p>
      </div>

      <div className="space-y-6">
        {/* Personal Information Review */}
        <div className="border rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Personal Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Name:</span>
              <span className="ml-2 text-gray-900">{formData.firstName} {formData.lastName}</span>
            </div>
            <div>
              <span className="text-gray-600">Email:</span>
              <span className="ml-2 text-gray-900">{formData.email}</span>
            </div>
            <div>
              <span className="text-gray-600">Phone:</span>
              <span className="ml-2 text-gray-900">{formData.phone}</span>
            </div>
            <div>
              <span className="text-gray-600">Country:</span>
              <span className="ml-2 text-gray-900">
                {countries.find(c => c.code === formData.country)?.name}
              </span>
            </div>
          </div>
        </div>

        {/* Address Information Review */}
        {(formData.address || formData.city) && (
          <div className="border rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Address Information</h3>
            <div className="text-sm text-gray-900">
              {formData.address && <div>{formData.address}</div>}
              <div>
                {formData.city && formData.city}
                {formData.city && formData.state && ', '}
                {formData.state && formData.state}
                {formData.zipCode && ` ${formData.zipCode}`}
              </div>
            </div>
          </div>
        )}

        {/* Tax Form Review */}
        <div className="border rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Tax Form</h3>
          <div className="text-sm">
            <span className="text-gray-600">Form Type:</span>
            <span className="ml-2 text-gray-900">
              {['US', 'PR'].includes(formData.country) ? 'W-9' : 'W-8BEN'} - Completed ✓
            </span>
          </div>
        </div>

        {identityRequired && (
          <div className="border rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Identity Verification</h3>
            <div className="text-sm">
              <span className="text-gray-600">Status:</span>
              <span className="ml-2 text-green-600 font-medium">
                {identityVerification?.status === 'suspected' ? 'Submitted for review ✓' : 'Verified ✓'}
              </span>
            </div>
          </div>
        )}

        {/* Agreement Review */}
        <div className="border rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Agent Agreement</h3>
          <div className="text-sm">
            <span className="text-gray-600">Status:</span>
            <span className="ml-2 text-green-600 font-medium">Agreed ✓</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 pt-6">
          <button
            type="button"
            onClick={() => setCurrentStep(1)}
            className="flex-1 py-3 px-4 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 font-medium transition-colors order-2 sm:order-1"
          >
            Edit Information
          </button>
          <button
            type="button"
            onClick={handleFinalSubmit}
            disabled={loading}
            className={`flex-1 py-3 px-4 rounded-md text-white font-medium transition-colors order-1 sm:order-2 ${
              loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500'
            }`}
          >
            {loading ? 'Submitting Application...' : 'Submit Application'}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full">
      {renderStepIndicator()}
      
      {stepKey === 'personal' && renderPersonalInfoStep()}
      {stepKey === 'identity' && (
        <IdentityVerificationStep
          agencyCode={agencyCode}
          formData={formData}
          verification={identityVerification}
          onChange={setIdentityVerification}
          phoneVerification={phoneVerification}
          onPhoneVerified={setPhoneVerification}
          onContinue={() => setCurrentStep(3)}
          onBack={() => setCurrentStep(1)}
        />
      )}
      {stepKey === 'tax' && renderTaxFormStep()}
      {stepKey === 'review' && renderReviewStep()}
    </div>
  );
};

// W9 Form wrapper for registration
const W9FormRegistration = ({ user, registrationData, onComplete, onBack }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    // Pre-fill from registration data
    name: `${registrationData.firstName} ${registrationData.lastName}`.trim(),
    businessName: '',
    federalTaxClassification: 'individual',
    llcClassification: '',
    otherDescription: '',
    exemptPayeeCode: '',
    exemptionFromFATCA: '',
    
    // Pre-fill address from registration
    address: registrationData.address || '',
    city: registrationData.city || '',
    state: registrationData.state || '',
    zipCode: registrationData.zipCode || '',
    
    // Requester info
    requesterName: 'Travel+ CRM',
    requesterAddress: '929 W. Sunset Blvd. STE 21-292, St. George, UT 84770',
    accountNumbers: '',
    
    // TIN
    tinType: 'ssn',
    ssn1: '', ssn2: '', ssn3: '',
    ein1: '', ein2: '',
    
    // Certification
    certification1: false,
    certification2: false,
    certification3: false,
    certification4: false,
    
    // Signature
    signature: '',
    signatureDate: new Date().toISOString().split('T')[0]
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.federalTaxClassification) newErrors.federalTaxClassification = 'Tax classification is required';
    if (!formData.address) newErrors.address = 'Address is required';
    if (!formData.city) newErrors.city = 'City is required';
    if (!formData.state) newErrors.state = 'State is required';
    if (!formData.zipCode) newErrors.zipCode = 'ZIP code is required';
    
    if (formData.tinType === 'ssn') {
      if (!formData.ssn1 || !formData.ssn2 || !formData.ssn3) {
        newErrors.ssn = 'SSN is required';
      } else if (formData.ssn1.length !== 3 || formData.ssn2.length !== 2 || formData.ssn3.length !== 4) {
        newErrors.ssn = 'Invalid SSN format';
      }
    } else {
      if (!formData.ein1 || !formData.ein2) {
        newErrors.ein = 'EIN is required';
      } else if (formData.ein1.length !== 2 || formData.ein2.length !== 7) {
        newErrors.ein = 'Invalid EIN format';
      }
    }
    
    if (!formData.certification1) newErrors.certification1 = 'You must certify backup withholding status';
    if (!formData.certification2) newErrors.certification2 = 'You must certify US person status';
    if (!formData.certification3) newErrors.certification3 = 'You must certify TIN accuracy';
    if (!formData.certification4) newErrors.certification4 = 'You must certify FATCA reporting status';
    if (!formData.signature) newErrors.signature = 'Signature is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const tin = formData.tinType === 'ssn' 
        ? `${formData.ssn1}-${formData.ssn2}-${formData.ssn3}`
        : `${formData.ein1}-${formData.ein2}`;
      
      const taxFormData = {
        form_type: 'W-9',
        form_data: {
          ...formData,
          tin,
          signatureDate: new Date().toISOString()
        },
        digital_signature: formData.signature
      };
      
      onComplete(taxFormData);
    } catch (error) {
      console.error('Form preparation error:', error);
      setErrors({ submit: 'Failed to prepare form. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          Form W-9: Request for Taxpayer Identification Number and Certification
        </h3>
        <p className="text-gray-600">
          Complete this form as part of your agent registration.
        </p>
      </div>

      {/* Part I - Identification */}
      <div className="border rounded-lg p-6">
        <h4 className="text-lg font-semibold mb-4">Part I - Taxpayer Identification</h4>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              1. Name (as shown on your income tax return) *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
              required
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              2. Business name/disregarded entity name, if different from above
            </label>
            <input
              type="text"
              name="businessName"
              value={formData.businessName}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              3. Federal tax classification *
            </label>
            <div className="space-y-2">
              {[
                { value: 'individual', label: 'Individual/sole proprietor or single-member LLC' },
                { value: 'c-corp', label: 'C Corporation' },
                { value: 's-corp', label: 'S Corporation' },
                { value: 'partnership', label: 'Partnership' },
                { value: 'trust', label: 'Trust/estate' },
                { value: 'llc', label: 'Limited liability company' },
                { value: 'other', label: 'Other' }
              ].map(option => (
                <label key={option.value} className="flex items-center">
                  <input
                    type="radio"
                    name="federalTaxClassification"
                    value={option.value}
                    checked={formData.federalTaxClassification === option.value}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  <span className="text-sm">{option.label}</span>
                </label>
              ))}
            </div>
            {errors.federalTaxClassification && (
              <p className="text-red-500 text-sm mt-1">{errors.federalTaxClassification}</p>
            )}
          </div>
        </div>
      </div>

      {/* Part II - Address */}
      <div className="border rounded-lg p-6">
        <h4 className="text-lg font-semibold mb-4">Part II - Address</h4>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              5. Address (number, street, and apt. or suite no.) *
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md ${errors.address ? 'border-red-500' : 'border-gray-300'}`}
              required
            />
            {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                6. City *
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md ${errors.city ? 'border-red-500' : 'border-gray-300'}`}
                required
              />
              {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State *
              </label>
              <select
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md ${errors.state ? 'border-red-500' : 'border-gray-300'}`}
                required
              >
                <option value="">Select State</option>
                {usStates.map(state => (
                  <option key={state.code} value={state.code}>
                    {state.name}
                  </option>
                ))}
              </select>
              {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state}</p>}
            </div>
          </div>

          <div className="w-1/2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ZIP code *
            </label>
            <input
              type="text"
              name="zipCode"
              value={formData.zipCode}
              onChange={handleInputChange}
              maxLength="10"
              className={`w-full px-3 py-2 border rounded-md ${errors.zipCode ? 'border-red-500' : 'border-gray-300'}`}
              required
            />
            {errors.zipCode && <p className="text-red-500 text-sm mt-1">{errors.zipCode}</p>}
          </div>
        </div>
      </div>

      {/* Part III - TIN */}
      <div className="border rounded-lg p-6">
        <h4 className="text-lg font-semibold mb-4">Part III - Taxpayer Identification Number (TIN)</h4>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select TIN type *
          </label>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="tinType"
                value="ssn"
                checked={formData.tinType === 'ssn'}
                onChange={handleInputChange}
                className="mr-2"
              />
              <span>Social Security Number (SSN)</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="tinType"
                value="ein"
                checked={formData.tinType === 'ein'}
                onChange={handleInputChange}
                className="mr-2"
              />
              <span>Employer Identification Number (EIN)</span>
            </label>
          </div>
        </div>

        {formData.tinType === 'ssn' ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Social Security Number *
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                name="ssn1"
                value={formData.ssn1}
                onChange={handleInputChange}
                maxLength="3"
                placeholder="XXX"
                className="w-20 px-3 py-2 border border-gray-300 rounded-md text-center"
              />
              <span className="self-center">-</span>
              <input
                type="text"
                name="ssn2"
                value={formData.ssn2}
                onChange={handleInputChange}
                maxLength="2"
                placeholder="XX"
                className="w-16 px-3 py-2 border border-gray-300 rounded-md text-center"
              />
              <span className="self-center">-</span>
              <input
                type="text"
                name="ssn3"
                value={formData.ssn3}
                onChange={handleInputChange}
                maxLength="4"
                placeholder="XXXX"
                className="w-24 px-3 py-2 border border-gray-300 rounded-md text-center"
              />
            </div>
            {errors.ssn && <p className="text-red-500 text-sm mt-1">{errors.ssn}</p>}
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Employer Identification Number *
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                name="ein1"
                value={formData.ein1}
                onChange={handleInputChange}
                maxLength="2"
                placeholder="XX"
                className="w-16 px-3 py-2 border border-gray-300 rounded-md text-center"
              />
              <span className="self-center">-</span>
              <input
                type="text"
                name="ein2"
                value={formData.ein2}
                onChange={handleInputChange}
                maxLength="7"
                placeholder="XXXXXXX"
                className="w-32 px-3 py-2 border border-gray-300 rounded-md text-center"
              />
            </div>
            {errors.ein && <p className="text-red-500 text-sm mt-1">{errors.ein}</p>}
          </div>
        )}
      </div>

      {/* Part IV - Certification */}
      <div className="border rounded-lg p-6">
        <h4 className="text-lg font-semibold mb-4">Part IV - Certification</h4>
        
        <p className="text-sm text-gray-600 mb-4">
          Under penalties of perjury, I certify that:
        </p>
        
        <div className="space-y-3">
          <label className="flex items-start">
            <input
              type="checkbox"
              name="certification1"
              checked={formData.certification1}
              onChange={handleInputChange}
              className="mt-1 mr-3"
            />
            <span className="text-sm">
              1. The number shown on this form is my correct taxpayer identification number, and
            </span>
          </label>
          {errors.certification1 && <p className="text-red-500 text-sm ml-6">{errors.certification1}</p>}

          <label className="flex items-start">
            <input
              type="checkbox"
              name="certification2"
              checked={formData.certification2}
              onChange={handleInputChange}
              className="mt-1 mr-3"
            />
            <span className="text-sm">
              2. I am not subject to backup withholding because: (a) I am exempt from backup withholding, or (b) I have not been notified by the IRS that I am subject to backup withholding, and
            </span>
          </label>
          {errors.certification2 && <p className="text-red-500 text-sm ml-6">{errors.certification2}</p>}

          <label className="flex items-start">
            <input
              type="checkbox"
              name="certification3"
              checked={formData.certification3}
              onChange={handleInputChange}
              className="mt-1 mr-3"
            />
            <span className="text-sm">
              3. I am a U.S. citizen or other U.S. person, and
            </span>
          </label>
          {errors.certification3 && <p className="text-red-500 text-sm ml-6">{errors.certification3}</p>}

          <label className="flex items-start">
            <input
              type="checkbox"
              name="certification4"
              checked={formData.certification4}
              onChange={handleInputChange}
              className="mt-1 mr-3"
            />
            <span className="text-sm">
              4. The FATCA code(s) entered on this form (if any) indicating that I am exempt from FATCA reporting is correct.
            </span>
          </label>
          {errors.certification4 && <p className="text-red-500 text-sm ml-6">{errors.certification4}</p>}
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Signature *
          </label>
          <input
            type="text"
            name="signature"
            value={formData.signature}
            onChange={handleInputChange}
            placeholder="Type your full name as digital signature"
            className={`w-full px-3 py-2 border rounded-md ${errors.signature ? 'border-red-500' : 'border-gray-300'}`}
            required
          />
          {errors.signature && <p className="text-red-500 text-sm mt-1">{errors.signature}</p>}
          
          <p className="text-sm text-gray-500 mt-2">
            Date: {new Date().toLocaleDateString()}
          </p>
        </div>
      </div>

      {errors.submit && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{errors.submit}</p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-4">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 py-3 px-4 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 font-medium transition-colors"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={loading}
          className={`flex-1 py-3 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            loading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {loading ? 'Processing...' : 'Continue'}
        </button>
      </div>
    </form>
  );
};

// W8BEN Form wrapper for registration
const W8BENFormRegistration = ({ user, registrationData, onComplete, onBack }) => {
  const [formData, setFormData] = useState({
    // Part I - Identification of Beneficial Owner
    name: `${registrationData.firstName} ${registrationData.lastName}`,
    countryOfCitizenship: '',
    permanentAddress: registrationData.address || '',
    city: registrationData.city || '',
    stateProvince: registrationData.state || '',
    postalCode: registrationData.zipCode || '',
    country: registrationData.country || '',
    mailingAddress: '',
    mailingCity: '',
    mailingStateProvince: '',
    mailingPostalCode: '',
    mailingCountry: '',
    
    // Part II - Claim of Tax Treaty Benefits
    usTaxpayerIdNumber: '',
    foreignTaxIdNumber: '',
    referenceNumber: '',
    dateOfBirth: '',
    
    // Part III - Certification
    signature: '',
    signatureDate: new Date().toISOString().split('T')[0],
    printName: `${registrationData.firstName} ${registrationData.lastName}`
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    const requiredFields = [
      'name', 'countryOfCitizenship', 'permanentAddress', 
      'city', 'country', 'dateOfBirth', 'signature', 'printName'
    ];
    
    requiredFields.forEach(field => {
      if (!formData[field]) {
        newErrors[field] = 'This field is required';
      }
    });
    
    // Date validation
    if (formData.dateOfBirth && new Date(formData.dateOfBirth) > new Date()) {
      newErrors.dateOfBirth = 'Date of birth cannot be in the future';
    }
    
    // Signature validation
    if (formData.signature && formData.signature !== formData.printName) {
      newErrors.signature = 'Signature must match printed name for digital signature';
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setLoading(false);
      return;
    }
    
    try {
      // Process W-8BEN form data
      const taxFormData = {
        form_type: 'w8ben',
        form_data: formData,
        digital_signature: formData.signature
      };
      
      await onComplete(taxFormData);
    } catch (error) {
      console.error('W-8BEN form submission error:', error);
      setErrors({ submit: 'Failed to submit form. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          Form W-8BEN: Certificate of Foreign Status
        </h3>
        <p className="text-gray-600">
          Complete this form as part of your agent registration. This form is required for non-U.S. persons.
        </p>
      </div>
      
      {/* Part I - Identification of Beneficial Owner */}
      <section className="space-y-6">
        <h4 className="text-lg font-semibold text-gray-900 border-b pb-2">
          Part I: Identification of Beneficial Owner
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Name (Individual or organization name) *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              required
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          <div>
            <label htmlFor="countryOfCitizenship" className="block text-sm font-medium text-gray-700 mb-2">
              Country of citizenship *
            </label>
            <input
              type="text"
              id="countryOfCitizenship"
              name="countryOfCitizenship"
              value={formData.countryOfCitizenship}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.countryOfCitizenship ? 'border-red-500' : 'border-gray-300'
              }`}
              required
            />
            {errors.countryOfCitizenship && <p className="mt-1 text-sm text-red-600">{errors.countryOfCitizenship}</p>}
          </div>
        </div>

        <div>
          <label htmlFor="permanentAddress" className="block text-sm font-medium text-gray-700 mb-2">
            Permanent residence address *
          </label>
          <input
            type="text"
            id="permanentAddress"
            name="permanentAddress"
            value={formData.permanentAddress}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.permanentAddress ? 'border-red-500' : 'border-gray-300'
            }`}
            required
          />
          {errors.permanentAddress && <p className="mt-1 text-sm text-red-600">{errors.permanentAddress}</p>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
              City *
            </label>
            <input
              type="text"
              id="city"
              name="city"
              value={formData.city}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.city ? 'border-red-500' : 'border-gray-300'
              }`}
              required
            />
            {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city}</p>}
          </div>

          <div>
            <label htmlFor="stateProvince" className="block text-sm font-medium text-gray-700 mb-2">
              State/Province
            </label>
            <input
              type="text"
              id="stateProvince"
              name="stateProvince"
              value={formData.stateProvince}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-2">
              Postal code
            </label>
            <input
              type="text"
              id="postalCode"
              name="postalCode"
              value={formData.postalCode}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div>
          <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
            Country *
          </label>
          <input
            type="text"
            id="country"
            name="country"
            value={formData.country}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.country ? 'border-red-500' : 'border-gray-300'
            }`}
            required
          />
          {errors.country && <p className="mt-1 text-sm text-red-600">{errors.country}</p>}
        </div>
      </section>

      {/* Part II - Claim of Tax Treaty Benefits */}
      <section className="space-y-6">
        <h4 className="text-lg font-semibold text-gray-900 border-b pb-2">
          Part II: Claim of Tax Treaty Benefits (if applicable)
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="usTaxpayerIdNumber" className="block text-sm font-medium text-gray-700 mb-2">
              U.S. taxpayer identification number (if any)
            </label>
            <input
              type="text"
              id="usTaxpayerIdNumber"
              name="usTaxpayerIdNumber"
              value={formData.usTaxpayerIdNumber}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="foreignTaxIdNumber" className="block text-sm font-medium text-gray-700 mb-2">
              Foreign tax identifying number (if any)
            </label>
            <input
              type="text"
              id="foreignTaxIdNumber"
              name="foreignTaxIdNumber"
              value={formData.foreignTaxIdNumber}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div>
          <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-2">
            Date of birth (MM/DD/YYYY) *
          </label>
          <input
            type="date"
            id="dateOfBirth"
            name="dateOfBirth"
            value={formData.dateOfBirth}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.dateOfBirth ? 'border-red-500' : 'border-gray-300'
            }`}
            required
          />
          {errors.dateOfBirth && <p className="mt-1 text-sm text-red-600">{errors.dateOfBirth}</p>}
        </div>
      </section>

      {/* Part III - Certification */}
      <section className="space-y-6">
        <h4 className="text-lg font-semibold text-gray-900 border-b pb-2">
          Part III: Certification
        </h4>
        
        <div className="bg-gray-50 p-4 rounded-md">
          <p className="text-sm text-gray-700 mb-4">
            Under penalties of perjury, I declare that I have examined the information on this form and to the best of my knowledge and belief it is true, correct, and complete. I further certify under penalties of perjury that:
          </p>
          <ul className="text-sm text-gray-700 space-y-2 ml-4">
            <li>• I am the individual that is the beneficial owner (or am authorized to sign for the individual that is the beneficial owner) of all the income to which this form relates or am using this form to document myself for chapter 4 purposes,</li>
            <li>• The person named on line 1 of this form is not a U.S. person,</li>
            <li>• The income to which this form relates is: (a) not effectively connected with the conduct of a trade or business in the United States, (b) effectively connected but is not subject to tax under an applicable income tax treaty, or (c) the partner's share of a partnership's effectively connected income, and</li>
            <li>• For broker transactions or barter exchanges, the beneficial owner is an exempt foreign person as defined in the instructions.</li>
          </ul>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="printName" className="block text-sm font-medium text-gray-700 mb-2">
              Print name of signer *
            </label>
            <input
              type="text"
              id="printName"
              name="printName"
              value={formData.printName}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.printName ? 'border-red-500' : 'border-gray-300'
              }`}
              required
            />
            {errors.printName && <p className="mt-1 text-sm text-red-600">{errors.printName}</p>}
          </div>

          <div>
            <label htmlFor="signatureDate" className="block text-sm font-medium text-gray-700 mb-2">
              Date *
            </label>
            <input
              type="date"
              id="signatureDate"
              name="signatureDate"
              value={formData.signatureDate}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              readOnly
            />
          </div>
        </div>

        <div>
          <label htmlFor="signature" className="block text-sm font-medium text-gray-700 mb-2">
            Sign Here (Type your full name as digital signature) *
          </label>
          <input
            type="text"
            id="signature"
            name="signature"
            value={formData.signature}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.signature ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Type your full name exactly as printed above"
            required
          />
          {errors.signature && <p className="mt-1 text-sm text-red-600">{errors.signature}</p>}
        </div>
      </section>

      {errors.submit && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{errors.submit}</p>
        </div>
      )}
      
      <div className="flex flex-col sm:flex-row gap-4">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 py-3 px-4 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 font-medium transition-colors"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={loading}
          className={`flex-1 py-3 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            loading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {loading ? 'Processing...' : 'Continue'}
        </button>
      </div>
    </form>
  );
};

export default AgencyRegistrationWithTax;
